import useCart from 'modules/cart/util/use-cart';
import {
  AdditionalGroupConfigTypeEnum,
  PriceModeEnum,
  ProductProps,
  SizeProps,
} from 'modules/products/types';
import React, {useMemo} from 'react';
import Row from 'shared/components/row';
import {MediumText} from 'shared/components/text';
import isNumber from 'shared/util/is-number';
import toBrl from 'shared/util/to-brl';

const ProductPrice: React.FC<ProductProps & {productIndex?: number}> = (
  props,
) => {
  const {
    priceMode,
    sizes,
    price,
    salePrice,
    productIndex,
    saleId,
    additionalGroupsConfig,
    remainingProducts,
  } = props;
  const {cart} = useCart();

  const cartProduct = cart?.products.find(
    (someProduct, index) =>
      index !== productIndex && !!someProduct.saleId && someProduct.onePerOrder,
  );

  const minSize = sizes?.reduce<SizeProps | undefined>((minSize, size) => {
    if (!minSize) return size;
    if (
      (cartProduct?.saleId !== minSize.saleId
        ? minSize.salePrice ?? minSize.price
        : minSize.price) >
      (cartProduct?.saleId !== size.saleId
        ? size.salePrice ?? size.price
        : size.price)
    )
      return size;

    return minSize;
  }, undefined);

  const isRemainingProductOnSale = useMemo(() => {
    if (!isNumber(remainingProducts)) return true;

    const cartSaleQuantity = (cart?.products ?? []).reduce(
      (sum, someProduct) =>
        someProduct.saleId &&
        (someProduct.saleId === saleId ||
          someProduct.saleId === minSize?.saleId)
          ? sum + someProduct.quantity
          : sum,
      0,
    );

    return (remainingProducts ?? 0) > cartSaleQuantity;
  }, [cart, minSize, remainingProducts, saleId]);

  const hideSizeSale =
    minSize?.saleId === cartProduct?.saleId || !isRemainingProductOnSale;

  const hideSale = saleId === cartProduct?.saleId || !isRemainingProductOnSale;

  const isSalePrice = !isNaN(Number(salePrice));
  const isSizeSale = !isNaN(Number(minSize?.salePrice));

  const additionalsPrice = additionalGroupsConfig.reduce(
    (total, additionalGroupConfig) => {
      const {min, additionalGroup, isFree, type} = additionalGroupConfig;

      if (min === 0 || isFree) return total;

      let minPrice = additionalGroup.additionals.reduce(
        (minPrice, {price = 0}) => Math.min(minPrice, price),
        Infinity,
      );

      if (minPrice === Infinity) minPrice = 0;

      return (
        total +
        (type === AdditionalGroupConfigTypeEnum.sum ? minPrice * min : minPrice)
      );
    },
    0,
  );

  return (
    <>
      {!hideSizeSale && !!minSize?.salePrice && (
        <Row gap={5}>
          <MediumText>A partir de</MediumText>
          <MediumText color="green">{toBrl(minSize.salePrice)}</MediumText>
          <MediumText
            color="opaqueText"
            size="small"
            style={{textDecorationLine: 'line-through'}}>
            {toBrl(minSize.price + additionalsPrice)}
          </MediumText>
        </Row>
      )}
      {priceMode === 'size' && (!isSizeSale || hideSizeSale) && (
        <MediumText>
          A partir de {toBrl(minSize?.price ?? 0 + additionalsPrice)}
        </MediumText>
      )}
      {priceMode === PriceModeEnum.additionals && (
        <MediumText>A partir de {toBrl(additionalsPrice)}</MediumText>
      )}
      {priceMode === 'fixed' && (!isSalePrice || hideSale) && price && (
        <MediumText>{toBrl(price + additionalsPrice)}</MediumText>
      )}
      {isSalePrice && !hideSale && (
        <Row gap={5}>
          <MediumText color="green">
            {toBrl(salePrice! + additionalsPrice)}
          </MediumText>
          <MediumText
            color="opaqueText"
            size="small"
            style={{textDecorationLine: 'line-through'}}>
            {toBrl(price + additionalsPrice)}
          </MediumText>
        </Row>
      )}
    </>
  );
};

export default ProductPrice;
